import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { NgbDropdown, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'doc-popover',
  standalone: true,
  imports: [CommonModule, NgbDropdownModule],
  template: `
    <div
      ngbDropdown
      #dropdown="ngbDropdown"
      container="body"
      [placement]="placement"
      [autoClose]="dismissable ? 'outside' : false"
      (openChange)="onOpenChange($event)"
      class="doc-popover-dropdown">
      <button
        #anchorRef
        type="button"
        class="doc-popover-anchor"
        ngbDropdownToggle
        tabindex="-1"
        aria-hidden="true"
        [style.left.px]="anchorLeft"
        [style.top.px]="anchorTop"
        [style.width.px]="anchorWidth"></button>

      <div
        ngbDropdownMenu
        class="popover doc-popover-panel"
        [ngClass]="styleClass"
        [ngStyle]="panelStyle"
        role="dialog">
        <div class="popover-arrow"></div>
        @if (showCloseIcon) {
          <button
            type="button"
            class="btn-close doc-popover-close"
            aria-label="Close"
            (click)="hide()"></button>
        }
        <div class="popover-body doc-popover-body" (click)="$event.stopPropagation()">
          <ng-content></ng-content>
        </div>
      </div>
    </div>
  `,
  styleUrl: './doc-popover.component.scss',
})
export class DocPopoverComponent {
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() styleClass = '';
  @Input() style: Record<string, string> | null = null;
  @Input() dismissable = true;
  @Input() showCloseIcon = false;
  /** ng-bootstrap placement, e.g. bottom-start | bottom-end */
  @Input() placement: string = 'bottom-end';
  /** Kept for callers that still pass it; ng-select panels stay in-popover. */
  @Input() outsideClickIgnore = '';

  @Output() onHide = new EventEmitter<void>();

  @ViewChild('dropdown') private dropdown!: NgbDropdown;
  @ViewChild('anchorRef') private anchorRef?: ElementRef<HTMLElement>;

  @HostBinding('class.doc-popover-host')
  readonly hostClass = true;

  anchorLeft = 0;
  anchorTop = 0;
  anchorWidth = 1;

  get panelStyle(): Record<string, string> | null {
    return this.style;
  }

  get visible(): boolean {
    return this.dropdown?.isOpen() ?? false;
  }

  toggle(event: Event): void {
    event?.stopPropagation?.();
    event?.preventDefault?.();

    // Closing: hide only — do not reposition (avoids a ghost panel flash).
    if (this.dropdown?.isOpen()) {
      this.hide();
      return;
    }

    this.positionAt(event);
    this.dropdown.open();
  }

  show(event: Event): void {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    this.positionAt(event);
    if (!this.dropdown.isOpen()) {
      this.dropdown.open();
    }
  }

  hide(): void {
    this.dropdown?.close();
  }

  onOpenChange(open: boolean): void {
    if (!open) {
      this.onHide.emit();
    }
  }

  private resolveTarget(event: Event): HTMLElement | null {
    let target = (event.currentTarget ?? event.target) as HTMLElement | null;
    while (target && target !== document.body) {
      if (target.tagName?.toLowerCase() === 'button') {
        return target;
      }
      target = target.parentElement;
    }
    return (event.currentTarget ?? event.target) as HTMLElement | null;
  }

  private positionAt(event: Event): void {
    const target = this.resolveTarget(event);
    let left = 0;
    let top = 0;
    let width = 1;

    if (target?.getBoundingClientRect) {
      const rect = target.getBoundingClientRect();
      left = rect.left;
      top = rect.bottom;
      width = Math.max(rect.width, 1);
    } else {
      const mouse = event as MouseEvent;
      left = mouse.clientX ?? 0;
      top = mouse.clientY ?? 0;
      width = 1;
    }

    this.anchorLeft = left;
    this.anchorTop = top;
    this.anchorWidth = width;

    // Apply immediately so ng-bootstrap measures the correct anchor on open.
    if (this.anchorRef?.nativeElement) {
      const el = this.anchorRef.nativeElement;
      el.style.position = 'fixed';
      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
      el.style.width = `${width}px`;
      el.style.height = '1px';
    }

    this.cdr.detectChanges();
  }
}
